import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

export interface CompanyIntegrationFatorH {
  enabled: boolean;
  hasKey: boolean;
  keyPreview: string | null;
  /** Bound FatorH workspace id, or null when not bound. */
  workspaceId: string | null;
}

export interface CompanyIntegrationFatorHPutResponse {
  ok: true;
  enabled: boolean;
  hasKey: boolean;
  workspaceId: string | null;
}

export class CompanyIntegrationFetchError extends Error {
  readonly name = 'CompanyIntegrationFetchError';
  constructor(
    message: string,
    readonly status: number,
    /** Mensagem legível vinda do backend ({ error }), quando houver. */
    readonly serverMessage?: string
  ) {
    super(message);
  }
}

function serverMessageOf(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'error' in error) {
    const m = (error as { error?: unknown }).error;
    if (typeof m === 'string' && m.trim()) return m;
  }
  return undefined;
}

async function fetchCompanyIntegration(
  companyId: string,
  signal?: AbortSignal
): Promise<CompanyIntegrationFatorH> {
  const res = await fetchClient<CompanyIntegrationFatorH>({
    method: 'GET',
    url: `/companies/${companyId}/integrations/fator-h`,
    signal,
  });
  if (res.error) {
    throw new CompanyIntegrationFetchError(
      `Failed to load integration status (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

export async function saveCompanyIntegration(
  companyId: string,
  payload: { enabled: boolean; apiKey?: string; workspaceId?: string }
): Promise<CompanyIntegrationFatorHPutResponse> {
  const res = await fetchClient<CompanyIntegrationFatorHPutResponse>({
    method: 'PUT',
    url: `/companies/${companyId}/integrations/fator-h`,
    data: payload,
  });
  if (res.error) {
    throw new CompanyIntegrationFetchError(
      `Failed to save integration (status ${res.error.status})`,
      res.error.status,
      serverMessageOf(res.error)
    );
  }
  return res.data;
}

export function companyIntegrationFatorHQueryOptions(companyId: string) {
  return queryOptions({
    queryKey: ['recruiter', 'companies', companyId, 'integrations', 'fator-h'] as const,
    queryFn: ({ signal }) => fetchCompanyIntegration(companyId, signal),
    staleTime: 30_000,
    retry: (failureCount, err) => {
      if (err instanceof CompanyIntegrationFetchError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
